import { Component, OnInit } from '@angular/core';
import { DeleteSegmentationComponent } from '../../crm/segmentation/delete-segmentation/delete-segmentation.component';
import { CrmServiceService } from '../../crm/service/crm-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductServiceService } from '../../products/service/product-service.service';
import { AnimationOptions } from 'ngx-lottie';

@Component({
  selector: 'app-preview-catalogue',
  templateUrl: './preview-catalogue.component.html',
  styleUrls: ['./preview-catalogue.component.scss']
})
export class PreviewCatalogueComponent implements OnInit {
  catalogue: any;
  businessDetails: any;
  businessLogo: any;
  businessName: any;
  loading = false;
  businessUrl: any;

  constructor(
    private productService: ProductServiceService,
    private crmService: CrmServiceService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.businessDetails = JSON.parse(localStorage.getItem('bDetails') || '{}');
    this.businessUrl = this.businessDetails.websiteUrl;
  }

  showHeader = true;
  isProtected = false;
  unlocked = false;
  enteredPwd = '';
  catalogueId: any;

  lockedAnimation: AnimationOptions = {
    path: 'assets/animations/locked.json',
  };

  unlockedAnimation: AnimationOptions = {
    path: 'assets/animations/locked.json',
  };

  showUnlockAnimation = false;

  ngOnInit(): void {
    const currentUrl = this.router.url;
    this.showHeader = currentUrl.startsWith('/admin/catalogue/preview-catalogue');

    this.route.paramMap.subscribe(params => {
      const slug = params.get('slug');

      if (slug) {
        const [nameSlug, id] = slug.split('--');
        this.catalogueId = id;

        this.isProtected = currentUrl.includes('/preview-catalogue-protected');

        if (!this.isProtected) {
          this.fetchCatalogue(this.catalogueId);
        }
      } else {
        this.productService.openSnack('No catalogue slug provided', 'Close');
        this.router.navigate(['admin/catalogue/list-catalogue']);
      }
    });
  }

  unlock(id: any) {
    if (!this.enteredPwd) {
      this.productService.openSnack('Please enter a password', 'Close');
      return;
    }

    this.productService.checkCataloguePassword(id, this.enteredPwd).subscribe({
      next: (res: any) => {
        if (res?.data === true) {
          this.showUnlockAnimation = true;

          setTimeout(() => {
            this.unlocked = true;
            this.fetchCatalogue(id);
          }, 1500);
        } else {
          this.productService.openSnack('Access denied: Incorrect password.', 'Close');
        }
      },
      error: (err: any) => {
        this.productService.openSnack(err.error?.message || 'Error validating password', 'Close');
      }
    });
  }

  openProductDetails(product: any) {
    const businessUrl = this.catalogue.businessUrl;
    const url = `${businessUrl}/details?id=${product.productId}`;
    window.open(url, '_blank');
  }

  fetchCatalogue(id: any): void {
    this.loading = true;
    this.productService.getCatalogueList('', id).subscribe({
      next: (res: any) => {
        this.catalogue = res.data[0];
        if (this.catalogue?.products?.length) {
          this.groupedProducts = this.getGroupedProducts();
        }
        this.loading = false;
      },
      error: (err: any) => {
        this.productService.openSnack(err.error.message || 'Failed to load catalogue', 'Close');
        this.loading = false;
      }
    });
  }

  close(): void {
    this.router.navigate(['admin/catalogue/list-catalogue']);
  }

  getInitials(name: string): string {
    return name ? name.trim().charAt(0).toUpperCase() : '';
  }

  getAspectRatioValue(ratio: string): string {
    return ratio || '1 / 1';
  }

  getFullAddress(): string {
    const addr = this.businessDetails.address;
    if (!addr) return '';
    return [addr.address, addr.city, addr.state, addr.country, addr.pincode].filter(Boolean).join(', ');
  }

  deleteCatalogue(): void {
    let dialog = this.crmService.openSocialBrand(
      DeleteSegmentationComponent,
      'auto',
      '26%',
      {
        heading: "Delete Catalogue",
        subText: "Are you sure you want to delete this catalogue?",
        secondaryButton: "Cancel",
        primaryButton: "Delete"
      }
    );

    dialog.afterClosed().subscribe((data: any) => {
      if (data.response !== 'cancel') {
        this.productService.deleteCatalogue(this.catalogue.id).subscribe({
          next: () => {
            this.productService.openSnack("Catalogue deleted successfully", 'Close');
            this.router.navigate(['admin/catalogue/list-catalogue']);
          },
          error: (err: any) => {
            this.productService.openSnack(err.error.message || 'Error deleting catalogue', 'Close');
          }
        });
      }
    });
  }

  groupedProducts: { [key: string]: any[] } = {};

  getGroupedProducts() {
    const grouped: { [key: string]: any[] } = {};
    this.catalogue.products.forEach(p => {
      const category =
        p.itemCategorisation?.itemCategories?.[0]?.refName || 'Others';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(p);
    });
    return grouped;
  }

  editCatalogue(): void {
    this.router.navigate(['/admin/catalogue/create-catalogue'], {
      state: { data: this.catalogue }
    });
  }

  printRightPanel() {
    const rightPanel = document.querySelector('.right-panel') as HTMLElement;
    if (!rightPanel) return;

    const clone = rightPanel.cloneNode(true) as HTMLElement;
    const imgs = clone.querySelectorAll('img');
    imgs.forEach(img => {
      if (img.src) {
        img.setAttribute('src', img.src);
      }
    });

    const popupWin = window.open('', '_blank');
    if (!popupWin) return;

    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map(el => el.outerHTML)
      .join('\n');

    const printCSS = `
       @page { size: A5; landscape}
      body {
        margin: 0;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      .print-container {
        display: flex;
        flex-direction: column;
        min-height: 100%;
      }
      .preview-product { page-break-inside: avoid; }
      img {
        page-break-inside: avoid;
        max-width: 100%;
        height: auto;
      }
      .footer {
        margin-top: auto;
        page-break-inside: avoid;
      }
    `;

    popupWin.document.open();
    popupWin.document.write(`
      <html>
        <head>
          <title>Catalogue Preview</title>
          ${styles}
          <style>${printCSS}</style>
        </head>
        <body>
          <div class="print-container">
            ${clone.innerHTML}
          </div>
          <script>
            window.onload = () => {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    popupWin.document.close();
  }
}
